import React, { useState } from 'react';
import { Lock, Phone, ArrowRight } from 'lucide-react';

interface LoginProps {
  onLogin: (whatsapp: string, password: string) => Promise<void>;
  tenantName?: string;
}

export const Login: React.FC<LoginProps> = ({ onLogin, tenantName }) => {
  const [whatsapp, setWhatsapp] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleWhatsappChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const trimmed = digits.replace(/^0+/, '');
    setWhatsapp(trimmed.slice(0, 13)); // mantem DDI/DDD, corta sufixos longos
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await onLogin(whatsapp, password);
    } catch (err: any) {
      setError(err?.message || 'Falha ao autenticar. Verifique suas credenciais.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-black flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with Logo */}
        <div className="bg-gray-50 p-8 flex flex-col items-center justify-center border-b border-gray-100">
          <div className="mb-4">
            <svg viewBox="0 0 200 60" className="h-16 w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Q/Key shape */}
              <path d="M30 10C18.954 10 10 18.954 10 30C10 41.046 18.954 50 30 50C35.5 50 40.5 47.8 44.1 44.1L50 50L55 45L49.1 39.1C52.8 35.5 55 30.5 55 30C55 18.954 46.046 10 35 10H30Z" fill="#294b29" stroke="#294b29" strokeWidth="5"/>
              <circle cx="30" cy="30" r="8" fill="white"/>
              <circle cx="38" cy="22" r="6" fill="#FF0000"/>
              {/* Text */}
              <text x="65" y="40" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="32" fill="#294b29">VISÃO</text>
           </svg>
           <div className="text-center -mt-2">
             <span className="text-2xl font-black tracking-tight text-[#294b29]">ALPHA</span>
           </div>
          </div>
          <h2 className="text-2xl font-bold text-[#294b29]">Bem-vindo de volta</h2>
          <p className="text-gray-500 text-sm mt-1">
            {tenantName ? `Tenant: ${tenantName}` : 'Acesse sua conta corporativa'}
          </p>
        </div>

        {/* Form */}
        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 ml-1">WhatsApp (login) – só DDD + número</label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 text-gray-400" size={20} />
                <input 
                  type="tel" 
                  value={whatsapp}
                  onChange={(e) => handleWhatsappChange(e.target.value)}
                  placeholder="11999999999"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#294b29] focus:border-[#294b29] outline-none transition-all"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 ml-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#294b29] focus:border-[#294b29] outline-none transition-all"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 text-[#294b29] focus:ring-[#294b29]" />
                <span className="text-gray-600">Lembrar de mim</span>
              </label>
              <a href="#" className="text-[#294b29] font-semibold hover:underline">Esqueceu a senha?</a>
            </div>

            <button 
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#294b29] hover:bg-[#1a301a] disabled:opacity-70 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95 shadow-lg shadow-green-900/20"
            >
              <span>{isLoading ? 'Entrando...' : 'Entrar no Sistema'}</span>
              <ArrowRight size={20} />
            </button>
          </form>
        </div>
        
        <div className="bg-gray-50 px-8 py-4 text-center text-xs text-gray-500 border-t border-gray-100">
          &copy; 2024 Visão Alpha. Todos os direitos reservados.
        </div>
      </div>
    </div>
  );
};
